/**
 * Lightweight toast notification system.
 * Uses a simple pub/sub pattern — no external dependencies.
 */

export type ToastType = "success" | "error" | "info";

export type Toast = {
	id: string;
	type: ToastType;
	message: string;
};

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let nextId = 0;

function notify() {
	for (const listener of listeners) {
		listener([...toasts]);
	}
}

export function addToast(type: ToastType, message: string, durationMs = 4000) {
	const id = String(++nextId);
	toasts = [...toasts, { id, type, message }];
	notify();

	if (durationMs > 0) {
		setTimeout(() => {
			removeToast(id);
		}, durationMs);
	}
}

export function removeToast(id: string) {
	toasts = toasts.filter((t) => t.id !== id);
	notify();
}

export function subscribe(listener: Listener): () => void {
	listeners.add(listener);
	listener([...toasts]);
	return () => {
		listeners.delete(listener);
	};
}

// Convenience helpers
export const toast = {
	success: (message: string) => addToast("success", message),
	error: (message: string) => addToast("error", message, 6000),
	info: (message: string) => addToast("info", message),
};
